import { useCallback } from 'react';
import { Control, useFieldArray } from 'react-hook-form';

import type { FormValues } from '../composeEmailModal.types';

export type AttachmentUpload = {
  name: string;
  absolute_uri: string;
  relative_uri: string;
  size: number;
};

type AttachmentFieldsAdapter = {
  length: number;
  map: (cb: (field: unknown, index: number) => unknown) => unknown[];
  get: (index: number) => { name: string; absolute_uri: string; size: number };
  remove: (index: number) => void;
};

type UseAttachmentsOptions = {
  control: Control<FormValues>;
};

type UseAttachmentsResult = {
  attachmentFieldsAdapter: AttachmentFieldsAdapter;
  handleAttachmentAdded: (attachment: AttachmentUpload) => void;
};

export function useAttachments({
  control,
}: UseAttachmentsOptions): UseAttachmentsResult {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'attachments_v2',
  });

  const attachmentFieldsAdapter: AttachmentFieldsAdapter = {
    length: fields.length,
    map: (cb) =>
      fields.map((field, index) =>
        cb(
          {
            name: field.prettyName,
            absolute_uri: field.absoluteUri,
            size: field.size,
          },
          index,
        ),
      ),
    get: (index) => ({
      name: fields[index]?.prettyName,
      absolute_uri: fields[index]?.absoluteUri,
      size: fields[index]?.size ?? 0,
    }),
    remove,
  };

  const handleAttachmentAdded = useCallback(
    (attachment: AttachmentUpload) => {
      append({
        url: attachment.relative_uri,
        prettyName: attachment.name,
        absoluteUri: attachment.absolute_uri,
        size: attachment.size,
      });
    },
    [append],
  );

  return { attachmentFieldsAdapter, handleAttachmentAdded };
}
